<script>
import PostService from '@/services/PostService';
import PrintOnSiteBtn from '@/components/printQueue/PrintOnSiteBtn.vue';
import HelperService from '@/services/HelperService';
import StringUtils from '@/util/StringUtils';

export default {
  name: 'PrintOnSiteView',
  components: { PrintOnSiteBtn },
  data() {
    return {
      helperService: new HelperService(),
      post: [],
      headers: [
        { label: `${this.$options.filters.term('prisoner')} Number`, key: 'prisoner_number' },
        { label: `${this.$options.filters.term('prisoner')}'s Assigned Name`, key: 'prisonerName' },
        { label: 'External Contact', key: 'visitorName' },
        { label: 'Status', key: 'status' },
        { label: 'Print Status', key: 'printStatus' },
        { label: 'Updated', key: 'updated_at' },
        { label: 'Reviewed By', key: 'reviewed_by' },
        { label: '', key: 'actions' },
      ],
      currentPage: 1,
      totalRows: 0,
      localFilters: {
        prisonerNumber: '',
        visitorEmail: '',
        status: '',
        printOnSite: true,
        sentForPrinting: false,
        delivered: false,
        postType: 'Physical',
        after: '',
        before: '',
        limit: 10,
        skip: 0,
        sort: '-updated_at',
      },
    };
  },
  watch: {
    currentPage(newPage) {
      this.localFilters.skip = (newPage - 1) * this.localFilters.limit;
      this.getPost();
    },
  },
  mounted() {
    this.localFilters.status = this.$route.query.status;
    this.localFilters.sentForPrinting = this.$route.query.sentForPrinting;
    this.localFilters.delivered = this.$route.query.delivered;

    this.getPost();
  },
  methods: {
    async getPost() {
      try {
        const filters = { ...this.localFilters };

        if (filters.before) {
          filters.before = this.helperService.setEndOfDay(filters.before);
        }

        const { data } = await PostService.getAll(filters);
        this.post = data?.data || [];
        this.totalRows = data?.count || 0;
      } catch (e) {
        console.error('Error in getPost:', e);
        this.$toast.error('Could not get post, please refresh and try again', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
      }
    },
    getReviewedBy(post) {
      if (post.status === 'Requested' || post.status === 'AwaitingConfirmation') {
        return '-'
      }
      if (post.reviewer && post.reviewer.name) {
        return StringUtils.toTitleCase(post.reviewer.name);
      }
      return 'System';
    },
    filterPost() {
      this.currentPage = 1;
      this.getPost();
    },
    handleRowClicked(item) {
      this.$router.push({ name: 'post-view', params: { id: item._id } });
    },
    getPrintStatusFromPost(post) {
      return PostService.getPrintStatusTextFromPostOptions(post.sent_for_printing, post.dispatched, post.delivered);
    },
  },
}
</script>

<template>
  <div>
    <print-on-site-btn
      v-if="localFilters.status === 'Approved'"
      :filters="localFilters"
      @pdf-generated="getPost"
    />
    <h4>Filters</h4>
    <b-row class="align-items-end">
      <b-col cols="12" sm="6" lg="3" class="mb-1">
        <label>{{ 'prisoner' | term(true) }} Number</label>
        <b-form-input
          v-model="localFilters.prisonerNumber"
          :placeholder="'Enter ' + $options.filters.term('prisoner') + ' number'"
          @input="filterPost"
        />
      </b-col>
      <b-col cols="12" sm="6" lg="3" class="mb-1">
        <label>External Contact Email <small>( full email needed )</small></label>
        <b-form-input
          v-model="localFilters.visitorEmail"
          placeholder="Enter external contact email"
          type="email"
          @input="filterPost"
        />
      </b-col>
      <b-col cols="12" sm="6" lg="3" class="mb-1">
        <label>Status</label>
        <b-form-select
          v-model="localFilters.status"
          @change="filterPost"
        >
          <b-form-select-option value="">All Post</b-form-select-option>
          <b-form-select-option value="Approved">Approved</b-form-select-option>
          <b-form-select-option value="AwaitingConfirmation">Needs Approval</b-form-select-option>
          <b-form-select-option value="Rejected">Rejected</b-form-select-option>
        </b-form-select>
      </b-col>
      <b-col cols="12" sm="6" lg="3" class="mb-1">
        <label>Sort</label>
        <b-form-select
          v-model="localFilters.sort"
          @change="filterPost"
        >
          <b-form-select-option value="updated_at">Updated (Oldest First)</b-form-select-option>
          <b-form-select-option value="-updated_at">Updated (Newest First)</b-form-select-option>
          <b-form-select-option value="prisoner_number">{{ $options.filters.term('prisoner') | capEachWord }} Number (A-Z)</b-form-select-option>
          <b-form-select-option value="-prisoner_number">{{ $options.filters.term('prisoner') | capEachWord }} Number (Z-A)</b-form-select-option>
          <b-form-select-option value="visitor.firstname">Visitor Name (A-Z)</b-form-select-option>
          <b-form-select-option value="-visitor.firstname">Visitor Name (Z-A)</b-form-select-option>
        </b-form-select>
      </b-col>
    </b-row>
    <b-row class="align-items-end">
      <b-col cols="12" sm="6" lg="3" class="mb-1">
        <label>From Date</label>
        <b-form-datepicker
          v-model="localFilters.after"
          :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
          close-button
          locale="en-uk"
          reset-button
          today-button
          @input="filterPost"
        />
      </b-col>
      <b-col cols="12" sm="6" lg="3" class="mb-1">
        <label>End Date</label>
        <b-form-datepicker
          v-model="localFilters.before"
          :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
          close-button
          locale="en-uk"
          reset-button
          today-button
          @input="filterPost"
        />
      </b-col>
    </b-row>

    <b-card no-body>
      <b-table
        :current-page="currentPage"
        :fields="headers"
        :items="post"
        hover
        responsive
        show-empty
        @row-clicked="handleRowClicked"
      >
        <template #cell(prisonerName)="data">
          {{ data.item.contact.firstname }} {{ data.item.contact.lastname }}
        </template>
        <template #cell(visitorName)="data">
          {{ data.item.visitor.firstname }} {{ data.item.visitor.lastname }}
        </template>
        <template #cell(status)="data">
          {{ data.item.status === 'AwaitingConfirmation' ? 'Needs Approval' : data.item.status }}
        </template>
        <template #cell(printStatus)="data">
          {{ getPrintStatusFromPost(data.item) }}
        </template>
        <template #cell(updated_at)="data">
          {{ data.item.updated_at | formatDateTime }}
        </template>
        <template #cell(reviewed_by)="data">
          {{ getReviewedBy(data.item) }}
        </template>
      </b-table>
      <b-pagination
        v-model="currentPage"
        :per-page="localFilters.limit"
        :total-rows="totalRows"
        class="px-1"
      />
    </b-card>
  </div>
</template>
